import { head, includes, join, pipe, reject } from "ramda";
import { getNonAdminFields } from "../../content/edit/optionalContentFieldsUtils";
import { isBlank } from "../../../util/ext";

//-- Optional fields for UploadMediaDialog
export const optionalFieldsForUpload = [
  { fieldName: ["ageRange"], label: "Age Range" },
  {
    fieldName: ["attribution"],
    label: "Attribution",
  },
  { fieldName: ["audience"], label: "Audience" },
  {
    fieldName: ["doi"],
    label: "DOI",
  },
  {
    fieldName: ["episode"],
    label: "Episode",
  },
  {
    fieldName: ["isbn"],
    label: "ISBN",
    inputProps: { maxLength: 13 },
  },
  {
    fieldName: ["issn"],
    label: "ISSN",
    inputProps: { maxLength: 8 },
  },
  {
    fieldName: ["keywords"],
    label: "Keywords",
    size: 6,
  },
  {
    fieldName: ["periodical"],
    label: "Periodical",
  },
  {
    fieldName: ["placeOfPublication"],
    label: "Place of Publication",
  },
  {
    fieldName: ["purchaseLink", "title"],
    label: "Purchase Title",
  },
  {
    fieldName: ["purchaseLink", "url"],
    label: "Purchase URL",
  },
  {
    fieldName: ["rating"],
    label: "Rating",
  },
  { fieldName: ["series"], label: "Series" },
  {
    fieldName: ["sourceUrl"],
    label: "Source URL",
  },
  {
    fieldName: ["volume"],
    label: "Volume",
  },
];

const optionalUploadFieldsToExclude = {
  ARTICLE: [
    "episode",
    "issueNumber",
    "pageRange",
    "periodical",
    "rating",
    "volume",
    "volumeNumber",
  ],
  AUDIO: [
    "isbn",
    "issn",
    "issueNumber",
    "keywords",
    "internalPublisherId",
    "rating",
    "textDirectionality",
    "volume",
  ],
  AUDIOBOOK: ["edition", "episode", "isbn", "issueNumber", "volume"],
  BOOK: ["edition", "episode", "isbn", "issueNumber", "rating", "volume"],
  IMAGE: [
    "episode",
    "isbn",
    "issn",
    "issueNumber",
    "periodical",
    "placeOfPublication",
    "rating",
    "series",
    "sourceUrl",
    "textDirectionality",
    "volume",
  ],
  OTHER_DOCUMENT: ["episode", "issueNumber", "periodical", "rating", "volume"],
  VIDEO: [
    "episode",
    "isbn",
    "issn",
    "issueNumber",
    "keywords",
    "periodical",
    "series",
    "textDirectionality",
    "volume",
  ],
};

export const getOptionalFieldsForUpload = (type, isAdmin) =>
  pipe(
    reject(field => {
      const exclusions = optionalUploadFieldsToExclude[type];
      const fieldName = head(field.fieldName);
      return includes(fieldName, exclusions);
    }),
    fields => (isAdmin ? fields : getNonAdminFields(fields)),
  )(optionalFieldsForUpload);

export const submissionTypes = {
  ARTICLE: {
    accepts: [
      ".PDF",
      ".jpg",
      ".png",
      "application/pdf",
      "image/jpeg",
      "image/png",
    ],
    displayAccepts: [".PDF", ".jpg", ".png"],

    displayName: "Article",
  },
  AUDIO: {
    accepts: [
      ".MP3",
      "audio/mpeg3",
      "audio/x-mpeg-3",
      ".MP4",
      "audio/mp4",
      ".wav",
      "audio/wav",
    ],
    displayAccepts: [".mp3", ".mp4", ".wav"],

    displayName: "Audio",
  },
  AUDIOBOOK: {
    accepts: [".ZIP", "application/zip", "application/x-zip-compressed"],
    displayAccepts: [".zip"],

    displayName: "Audiobook",
  },
  BOOK: {
    accepts: [".PDF", ".ePUB", "application/pdf", "application/epub+zip"],
    displayAccepts: ["ePub2", "ePub3", ".PDF"],

    displayName: "Book",
  },
  IMAGE: {
    accepts: [
      ".jpg",
      ".jpeg",
      "image/jpeg",
      ".png",
      "image/png",
      ".gif",
      "image/gif",
      ".tif",
      ".tiff",
      "image/tiff",
    ],
    displayAccepts: [".jpeg", ".jpg", ".gif", ".png", ".tiff", ".tif"],

    displayName: "Image",
  },
  OTHER_DOCUMENT: {
    accepts: [
      ".EPUB",
      "application/epub+zip",
      ".PDF",
      "application/pdf",
      ".jpg",
      "image/jpeg",
      ".DOCX",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ],
    displayAccepts: ["ePub2", "ePub3", ".PDF", ".docx", ".jpg"],

    displayName: "Document",
  },
  VIDEO: {
    accepts: [
      ".MP4",
      "video/mp4",
      ".MPG",
      "video/mpeg",
      ".MOV",
      "video/quicktime",
      ".AVI",
      "video/x-msvideo",
      ".3GP",
      "video/3gpp",
      ".FLV",
      "video/x-flv",
      ".DIVX",
      "video/divx",
      ".WEBM",
      "video/webm",
      ".OGG",
      "video/ogg",
    ],
    displayAccepts: [
      ".mp4",
      ".mpg",
      ".mov",
      ".avi",
      ".3gp",
      ".flv",
      ".divx",
      ".webm",
      ".ogg",
    ],
    displayName: "Video",
  },
};

export const getAcceptedFileTypes = type =>
  join(", ", submissionTypes[type].accepts);

export const displayAcceptedMimeTypes = type => {
  const mimeTypes = join(", ", submissionTypes[type].displayAccepts);
  return `Accepted file types: ${mimeTypes}`;
};

// fileType: "SUBMISSION" | "COVER"
export const mapUploadFileData = (fileType, file) => ({
  filename: file.name,
  fileType: fileType,
  mimeType: file.type,
  size: file.size,
});

export const formatBytes = (bytes, decimals = 2) => {
  if (!+bytes) return "0 Bytes";

  const k = 1000;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB"];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
};

export const formatTimeRemaining = secs => {
  if (secs === 0 || isBlank(secs) || isNaN(secs)) {
    return "";
  } else if (secs >= 60) {
    const minutes = Math.floor(secs / 60);
    return `${minutes} min left`;
  } else {
    return `${secs} secs left`;
  }
};
